import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileDown, Truck, FileText, ClipboardList } from "lucide-react";
import { api, eur, dt, errMsg, download, isProfessionista } from "@/lib/api";
import { useAuth, isAgent, canWrite } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MailButton } from "@/components/MailButton";

export const STATO_PREV = {
  bozza: ["Bozza", "bg-slate-100 text-slate-700"], inviato: ["Inviato", "bg-sky-50 text-sky-700"], accettato: ["Accettato", "bg-emerald-50 text-emerald-700"],
  rifiutato: ["Rifiutato", "bg-rose-50 text-rose-700"], scaduto: ["Scaduto", "bg-amber-50 text-amber-700"],
};
const CONV = [["ordine", "Ordine", ClipboardList, "/app/ordini"], ["fattura", "Fattura", FileText, "/app/fatture"], ["ddt", "DDT", Truck, "/app/ddt"]];

const Links = ({ p }) => (
  <>
    {p.ordine_id && <Link to={`/app/ordini/${p.ordine_id}`} className="ml-2 text-xs text-sky-700 underline">{p.ordine_numero}</Link>}
    {p.invoice_id && <Link to={`/app/fatture/${p.invoice_id}`} className="ml-2 text-xs text-sky-700 underline">Fattura {p.invoice_numero}</Link>}
    {p.ddt_id && <Link to={`/app/ddt/${p.ddt_id}`} className="ml-2 text-xs text-sky-700 underline">DDT {p.ddt_numero}</Link>}
  </>
);

const StatoPick = ({ p, onChange }) => (
  <Select value={["bozza", "inviato", "accettato", "rifiutato"].includes(p.stato) ? p.stato : undefined} onValueChange={(v) => onChange(p, v)}>
    <SelectTrigger data-testid={`quote-status-${p.id}`} className={`h-8 w-32 border-0 text-xs ${STATO_PREV[p.stato][1]}`}><SelectValue placeholder={STATO_PREV[p.stato][0]} /></SelectTrigger>
    <SelectContent>{["bozza", "inviato", "accettato", "rifiutato"].map((k) => <SelectItem key={k} value={k}>{STATO_PREV[k][0]}</SelectItem>)}</SelectContent>
  </Select>
);

export default function Preventivi() {
  const { user } = useAuth();
  const agent = isAgent(user);
  const write = canWrite(user);
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [stato, setStato] = useState("all");
  const [noDdt, setNoDdt] = useState(false);
  useEffect(() => {
    if (!agent) api.get("/company").then((r) => setNoDdt(isProfessionista(r.data) && !r.data.agenti_attivo)).catch(() => {});
  }, [agent]);
  const load = useCallback(() => api.get("/preventivi", { params: { stato: stato === "all" ? undefined : stato } }).then((r) => setItems(r.data)), [stato]);
  useEffect(() => { load(); }, [load]);
  const setSt = async (p, v) => { try { await api.patch(`/preventivi/${p.id}/stato`, { stato: v }); toast.success(`Preventivo ${STATO_PREV[v][0].toLowerCase()}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const conv = async (p, [k, l, , path]) => {
    if (!window.confirm(`Creare ${{ ordine: "l'ordine", fattura: "la fattura (in bozza)", ddt: "il DDT" }[k]} dal preventivo ${p.numero}?`)) return;
    try { const { data } = await api.post(`/preventivi/${p.id}/${k}`); toast.success(`${l} ${data.numero} creato`); nav(`${path}/${data.id}${k === "fattura" ? "/modifica" : ""}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const done = (p) => p.ordine_id || p.invoice_id || p.ddt_id;
  return (
    <div>
      <PageHeader title={agent ? "I miei preventivi" : "Preventivi"} subtitle="Prepara e invia i preventivi; quando il cliente accetta trasformali in ordine, fattura o DDT">
        <Select value={stato} onValueChange={setStato}>
          <SelectTrigger data-testid="quotes-filter-status" className="w-40 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti</SelectItem>{Object.entries(STATO_PREV).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        {write && <Button asChild className="bg-sky-600 hover:bg-sky-700" data-testid="new-quote-button"><Link to="/app/preventivi/nuovo"><Plus className="mr-2 h-4 w-4" />Nuovo preventivo</Link></Button>}
      </PageHeader>
      {items === null ? null : items.length === 0 ? <Empty text="Nessun preventivo" /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <Table>
            <TableHeader><TableRow><TableHead>Numero</TableHead><TableHead>Data</TableHead><TableHead>Validità</TableHead><TableHead>Cliente</TableHead>{!agent && <TableHead>Inserito da</TableHead>}<TableHead>Stato</TableHead><TableHead className="text-right">Totale</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`quote-row-${p.id}`}>
                  <TableCell><Link to={`/app/preventivi/${p.id}`} className="font-mono text-sm font-semibold text-sky-700 hover:underline">{p.numero}</Link></TableCell>
                  <TableCell className="text-sm">{dt(p.data)}</TableCell>
                  <TableCell className="text-sm">{p.validita ? dt(p.validita) : "-"}</TableCell>
                  <TableCell className="text-sm">{p.controparte?.denominazione || `${p.controparte?.nome || ""} ${p.controparte?.cognome || ""}`}</TableCell>
                  {!agent && <TableCell className="text-sm">{p.agente_nome}</TableCell>}
                  <TableCell className="whitespace-nowrap">
                    {write && !done(p) ? <StatoPick p={p} onChange={setSt} /> : <span className={`rounded px-2 py-0.5 text-xs ${STATO_PREV[p.stato][1]}`}>{STATO_PREV[p.stato][0]}</span>}
                    <Links p={p} />
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">{eur(p.totals.totale)}</TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <Button variant="ghost" size="icon" title="PDF" data-testid={`quote-pdf-${p.id}`} onClick={() => download(`/preventivi/${p.id}/pdf`).catch((e) => toast.error(e.message))}><FileDown className="h-4 w-4" /></Button>
                    {write && <MailButton icon path={`/preventivi/${p.id}/email`} email={p.controparte?.email} what="il preventivo" testid={`quote-email-${p.id}`} onSent={load} />}
                    {!agent && write && p.stato === "accettato" && CONV.filter((c) => !(noDdt && c[0] === "ddt") && !p[{ ordine: "ordine_id", fattura: "invoice_id", ddt: "ddt_id" }[c[0]]]).map((c) => (
                      <Button key={c[0]} size="sm" variant="outline" className="ml-1" data-testid={`quote-to-${c[0]}-${p.id}`} onClick={() => conv(p, c)}>{(() => { const I = c[2]; return <I className="mr-1 h-4 w-4" />; })()}{c[1]}</Button>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
